import AbstractTemplateDetailsPage from './abstract-template-details-page';
import { formatCurrency, formatNumber } from '../../helpers/utils';
import HTMLTemplateHelper from '../../helpers/html-template-helper';
import SlimSelect from '../../../lib/slim-select-custom';
import MessageHelper from '../../helpers/message-helper';

interface CustomService {
  code: string;
  name: string;
}

export default class OfferDetailsPage extends AbstractTemplateDetailsPage {

  private readonly addServiceButton: HTMLButtonElement | null;
  private readonly discountTotalSpan: HTMLSpanElement;
  private readonly tireWarrantyCheckboxContainer: HTMLDivElement;
  private readonly tireWarrantyCheckboxInput: HTMLInputElement;
  private readonly includeVatCheckboxInput: HTMLInputElement | null;
  private readonly priceLabelTableHeader: HTMLTableHeaderCellElement;
  private readonly customServiceSelect: HTMLSelectElement | null;
  private readonly customService?: SlimSelect;

  constructor(protected container: HTMLElement) {
    super(container, false);

    this.addServiceButton = container.querySelector('.js-addService');
    this.discountTotalSpan = container.querySelector('.js-discountTotal') as HTMLSpanElement;
    this.tireWarrantyCheckboxContainer = container.querySelector('.js-tireWarrantyCheckbox') as HTMLDivElement;
    this.tireWarrantyCheckboxInput = this.tireWarrantyCheckboxContainer.querySelector('input') as HTMLInputElement;
    this.includeVatCheckboxInput = container.querySelector('.js-includeVatCheckbox');
    this.priceLabelTableHeader = container.querySelector('.js-priceLabel') as HTMLTableHeaderCellElement;

    this.customServiceSelect = container.querySelector('.js-customServices');

    if (this.addServiceButton) {
      this.bindAddService();
    }
    if (this.customServiceSelect) {

      this.customService = new SlimSelect({
        select: this.customServiceSelect,
        showSearch: false,
      });
      this.bindAddServiceFromList();
    }
    this.preventSubmitWithEnterKey(this.container);
    this.bindVatToggle();
    this.recalculatePrice();
  }

  // eslint-disable-next-line class-methods-use-this
  private preventSubmitWithEnterKey(container: HTMLElement): void {
    Array.from(container.querySelectorAll<HTMLInputElement>('input[name]'))
      .forEach((input) => input.addEventListener(('keydown'), (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }));
  }

  private bindAddService(): void {
    this.addServiceButton?.addEventListener('click', () => {
      this.addRow();
    });
  }

  private bindAddServiceFromList(): void {
    this.customServiceSelect?.addEventListener('change', (event) => {
      const selectedOption = (event.target as HTMLSelectElement)?.querySelector<HTMLOptionElement>(':checked');
      if (selectedOption?.value) {
        const service: CustomService = {
          code: selectedOption.value,
          name: selectedOption.text,
        };
        this.addServiceRow(this.entriesContainer.children.length, service);
        this.customService?.set('');
      }
    });
  }

  private bindVatToggle(): void {
    this.includeVatCheckboxInput?.addEventListener('change', () => {
      this.recalculatePrice();
      this.updatePriceLabel();
    });
  }

  private addServiceRow(index: number, service: CustomService): void {
    const customServiceRowHtml = this.rowTemplate({ index, service });
    const row = HTMLTemplateHelper.createFromString(customServiceRowHtml) as HTMLTableRowElement;
    this.entriesContainer.append(row);
    this.bindRowEventListeners(row);
    this.handleRowChange();
  }

  protected bindRowEventListeners(row: HTMLTableRowElement): void {
    super.bindRowEventListeners(row);
    this.preventSubmitWithEnterKey(row);
  }

  protected handleRowChange(): void {
    super.handleRowChange();
    this.toggleTireWarrantyCheckboxVisibility();
  }

  private toggleTireWarrantyCheckboxVisibility(): void {
    const hasHireWarrantyProduct = document.querySelector('tr[data-valid-for-tire-warranty="true"]') !== null;
    this.tireWarrantyCheckboxContainer.classList.toggle('d-none', !hasHireWarrantyProduct);
    this.tireWarrantyCheckboxInput.disabled = !hasHireWarrantyProduct;
  }

  // eslint-disable-next-line class-methods-use-this
  protected getPriceInputSelector(): string {
    return '.js-price, .js-discount, .js-qty-selector-input';
  }

  protected recalculatePrice(): void {
    if (this.isVatIncluded()) {
      let grossTotalWithDiscount = 0;
      let discountTotal = 0;

      Array.from(this.entriesContainer.children)
        .forEach((tr) => {
          const priceInput = tr.querySelector('.js-price') as HTMLInputElement;
          const discountInput = tr.querySelector('.js-discount') as HTMLInputElement;
          const quantityInput = tr.querySelector('.js-qty-selector-input') as HTMLInputElement;
          const rowTotalSpan = tr.querySelector('.js-rowTotal') as HTMLSpanElement;

          const rowTotalWithoutDiscount = Number(priceInput.value ?? 0) * Number(quantityInput.value);
          const discountMultiplier = (100 - Number(discountInput.value ?? 0)) / 100;
          const rowTotal = rowTotalWithoutDiscount * discountMultiplier;

          grossTotalWithDiscount += rowTotal;
          discountTotal += (rowTotal - rowTotalWithoutDiscount);

          rowTotalSpan.textContent = formatNumber(rowTotal, 2);
        });

      const taxTotal = Math.round(grossTotalWithDiscount * ACC.taxRate * 100) / 100;

      this.discountTotalSpan.textContent = formatCurrency(Math.abs(discountTotal));
      this.netTotalSpan.textContent = formatCurrency(grossTotalWithDiscount - taxTotal);
      this.taxTotalSpan.textContent = formatCurrency(taxTotal);
      this.grandTotalSpan.textContent = formatCurrency(grossTotalWithDiscount);
    } else {
      let netTotalWithDiscount = 0;
      let discountTotal = 0;

      Array.from(this.entriesContainer.children)
        .forEach((tr) => {
          const priceInput = tr.querySelector('.js-price') as HTMLInputElement;
          const discountInput = tr.querySelector('.js-discount') as HTMLInputElement;
          const quantityInput = tr.querySelector('.js-qty-selector-input') as HTMLInputElement;
          const rowTotalSpan = tr.querySelector('.js-rowTotal') as HTMLSpanElement;

          const rowNetTotal = Number(priceInput.value ?? 0) * Number(quantityInput.value);
          const discountMultiplier = (100 - Number(discountInput.value ?? 0)) / 100;
          const rowTotal = rowNetTotal * discountMultiplier;

          netTotalWithDiscount += rowTotal;
          discountTotal += (rowTotal - rowNetTotal);

          rowTotalSpan.textContent = formatNumber(rowTotal, 2);
        });

      const taxTotal = Math.round(netTotalWithDiscount * ACC.taxRate * 100) / 100;

      this.discountTotalSpan.textContent = formatCurrency(Math.abs(discountTotal));
      this.netTotalSpan.textContent = formatCurrency(netTotalWithDiscount);
      this.taxTotalSpan.textContent = formatCurrency(taxTotal);
      this.grandTotalSpan.textContent = formatCurrency(netTotalWithDiscount + taxTotal);
    }
  }

  private isVatIncluded(): boolean {
    return Boolean(this.includeVatCheckboxInput?.checked);
  }

  private updatePriceLabel(): void {
    const message = MessageHelper.getMessage(this.isVatIncluded() ? 'checkout.webreturns.returnConfirmation.totalPrice' : 'common.salesPrice');
    const span = this.priceLabelTableHeader.querySelector('span') as HTMLSpanElement;
    this.priceLabelTableHeader.title = message; // eslint-disable-line no-param-reassign
    span.textContent = message; // eslint-disable-line no-param-reassign
  }
}
